import React, { useContext } from "react";
import currency from "currency.js";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import {
	PurchaseCorrectionApi,
	PurchaseCorrectionItemApi,
} from "../../../../../../../../../../../../../services/Api/Organization/types";
import { getRecalculatedSummaryTaxes } from "../../../../../../../utils/summaryTaxesUtils";
import { PurchaseContext } from "../../../../../pages/Preview";
import { getPreviousCorrection } from "../utils";

interface Props {
	watchedItems: PurchaseCorrectionItemApi[];
	form: UseFormReturn<PurchaseCorrectionApi>;
}

const PurchaseCorrectionFormItemsTableSummary = ({ watchedItems, form }: Props) => {
	const { t } = useTranslation();
	const { watch } = form;
	const { purchase, purchaseCorrections } = useContext(PurchaseContext);

	const getTotalPriceNetAfter = () => {
		const amount = getRecalculatedSummaryTaxes(watchedItems, purchase.tax_gross).reduce(
			(acc, cur) => currency(acc).add(cur.price_net.amount).value,
			0
		);

		return { amount, currency: purchase.currency };
	};

	const getTotalPriceGrossAfter = () => {
		const amount = getRecalculatedSummaryTaxes(watchedItems, purchase.tax_gross).reduce(
			(acc, cur) => currency(acc).add(cur.price_gross.amount).value,
			0
		);

		return { amount, currency: purchase.currency };
	};

	const getTotalPriceGrossBefore = () => {
		if (
			!purchaseCorrections ||
			purchaseCorrections.length < 1 ||
			!getPreviousCorrection(watch("id"), purchaseCorrections)
		) {
			return purchase.total_price_gross_money;
		}

		const previousCorrection = getPreviousCorrection(watch("id"), purchaseCorrections);
		if (previousCorrection) {
			return previousCorrection.total_price_gross_money;
		}

		return { amount: 0, currency: purchase.currency };
	};

	const getTotalPriceNetBefore = () => {
		if (
			!purchaseCorrections ||
			purchaseCorrections.length < 1 ||
			!getPreviousCorrection(watch("id"), purchaseCorrections)
		) {
			return purchase.total_price_net_money;
		}

		const previousCorrection = getPreviousCorrection(watch("id"), purchaseCorrections);
		if (previousCorrection) {
			return previousCorrection.total_price_net_money;
		}

		return { amount: 0, currency: purchase.currency };
	};

	const getPriceNetDifferential = () => {
		return {
			amount: currency(getTotalPriceNetAfter().amount).subtract(getTotalPriceNetBefore().amount).value,
			currency: purchase.currency,
		};
	};

	const getPriceGrossDifferential = () => {
		return {
			amount: currency(getTotalPriceGrossAfter().amount).subtract(getTotalPriceGrossBefore().amount).value,
			currency: purchase.currency,
		};
	};

	return (
		<>
			<tr className="summary with-padding">
				<td colSpan={3} />
				<td colSpan={3}>{t("modules.purchase.field.total_price_net_before.title")}</td>
				<td colSpan={2} className="text-end">
					{FormatMoney(getTotalPriceNetBefore())}
				</td>
				<td className="action" />
			</tr>
			<tr className="summary">
				<td colSpan={3} />
				<td colSpan={3}>{t("modules.purchase.field.total_price_net_after.title")}</td>
				<td colSpan={2} className="text-end">
					{FormatMoney(getTotalPriceNetAfter())}
				</td>
				<td className="action" />
			</tr>
			<tr className="summary">
				<td colSpan={3} />
				<td colSpan={3}>{t("modules.purchase.field.total_price_gross_before.title")}</td>
				<td colSpan={2} className="text-end">
					{FormatMoney(getTotalPriceGrossBefore())}
				</td>
				<td className="action" />
			</tr>
			<tr className="summary">
				<td colSpan={3} />
				<td colSpan={3}>{t("modules.purchase.field.total_price_gross_after.title")}</td>
				<td colSpan={2} className="text-end">
					{FormatMoney(getTotalPriceGrossAfter())}
				</td>
				<td className="action" />
			</tr>
			<tr className="summary">
				<td colSpan={3} />
				<td colSpan={3}>{t("modules.purchase.field.price_net_differential.title")}</td>
				<td colSpan={2} className="text-end">
					{FormatMoney(getPriceNetDifferential())}
				</td>
				<td className="action" />
			</tr>
			<tr className="summary">
				<td colSpan={3} />
				<td colSpan={3}>{t("modules.purchase.field.price_gross_differential.title")}</td>
				<td colSpan={2} className="text-end">
					{FormatMoney(getPriceGrossDifferential())}
				</td>
				<td className="action" />
			</tr>
		</>
	);
};

export default PurchaseCorrectionFormItemsTableSummary;
